import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { finalize } from 'rxjs/operators';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CatalogService, CatalogOffering } from '../catalog/catalog.service';
import { CommercialApiService, IndustrySolution } from '../commercial/commercial-api.service';
import {
  CreateStorefrontFormCatalogItemRequest,
  DynamicApiService,
  DynamicFormPublication,
  StorefrontFormCatalogItem,
  UpdateStorefrontFormCatalogItemRequest,
  UpsertStorefrontFormCatalogOfferingLinkRequest
} from './dynamic-api.service';

type SelectOption = { label: string; value: string };

@Component({
  standalone: true,
  selector: 'ain-storefront-form-catalog',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    FormsModule,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    TextareaModule,
    SelectModule,
    MultiSelectModule,
    CheckboxModule,
    InputNumberModule,
    ConfirmDialogModule,
    RouterLink
  ],
  providers: [ConfirmationService, MessageService],
  template: `
    <ain-page [title]="t('dynamic.storefrontCatalog.title')" [description]="t('dynamic.storefrontCatalog.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'dynamic.storefrontCatalog.infoTitle'"
        [descriptionKey]="'dynamic.storefrontCatalog.infoDescription'"
        [bulletKeys]="[
          'dynamic.storefrontCatalog.infoBulletCurated',
          'dynamic.storefrontCatalog.infoBulletPublication',
          'dynamic.storefrontCatalog.infoBulletOfferings'
        ]" />

      <section class="aggregate-panel">
        <div class="aggregate-panel__head">
          <h2>{{ t('dynamic.storefrontCatalog.industrySolutions.title') }}</h2>
          <p>{{ t('dynamic.storefrontCatalog.industrySolutions.description') }}</p>
        </div>
        @if (industrySolutionsLoading()) {
          <ain-state type="loading" [title]="t('common.loading')" />
        } @else if (industrySolutionsError()) {
          <ain-state type="error" [title]="t('common.loadFailed')" [message]="industrySolutionsError() ?? ''" />
        } @else if (!industrySolutions().length) {
          <ain-state type="empty" [title]="t('dynamic.storefrontCatalog.industrySolutions.empty')" />
        } @else {
          <p-table [value]="industrySolutions()" dataKey="id" [paginator]="true" [rows]="10">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('dynamic.storefrontCatalog.industrySolutions.columns.title') }}</th>
                <th>{{ t('dynamic.storefrontCatalog.industrySolutions.columns.explicitCount') }}</th>
                <th>{{ t('dynamic.storefrontCatalog.industrySolutions.columns.fallbackEnabled') }}</th>
                <th>{{ t('dynamic.storefrontCatalog.industrySolutions.columns.resolvedSource') }}</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.title }}</td>
                <td>{{ row.assessmentLinks.length }}</td>
                <td>{{ row.useOfferingAssessmentFallback ? t('common.yes') : t('common.no') }}</td>
                <td>{{ resolvedSourceLabel(row) }}</td>
                <td>
                  <a pButton severity="secondary" size="small" [label]="t('dynamic.storefrontCatalog.industrySolutions.manageAction')" [routerLink]="['/marketplace/industry-solutions']"></a>
                </td>
              </tr>
            </ng-template>
          </p-table>
        }
      </section>

      <div class="toolbar">
        <button pButton type="button" icon="pi pi-plus" [label]="t('common.create')" (click)="openCreateDialog()"></button>
        <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="load()"></button>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.loadFailed')" [message]="error() ?? ''" />
      } @else if (!items().length) {
        <ain-state type="empty" [title]="t('dynamic.storefrontCatalog.empty')" />
      } @else {
        <p-table [value]="items()" dataKey="id" [paginator]="true" [rows]="15">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('dynamic.storefrontCatalog.columns.title') }}</th>
              <th>{{ t('dynamic.storefrontCatalog.columns.category') }}</th>
              <th>{{ t('dynamic.storefrontCatalog.columns.slug') }}</th>
              <th>{{ t('dynamic.storefrontCatalog.columns.publication') }}</th>
              <th>{{ t('dynamic.storefrontCatalog.columns.visible') }}</th>
              <th>{{ t('dynamic.storefrontCatalog.columns.offerings') }}</th>
              <th></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr [class.row-warning]="!row.publicationIsPublished">
              <td>{{ row.displayTitle }}</td>
              <td>{{ categoryLabel(row.categoryKey) }}</td>
              <td>{{ row.publicationSlug }}</td>
              <td>
                @if (row.publicationIsPublished) {
                  {{ t('dynamic.storefrontCatalog.publicationPublished') }}
                } @else {
                  {{ t('dynamic.storefrontCatalog.publicationUnpublished') }}
                }
              </td>
              <td>{{ row.isVisible ? t('common.yes') : t('common.no') }}</td>
              <td>{{ row.offerings.length }}</td>
              <td class="actions">
                <button pButton type="button" severity="secondary" size="small" [label]="t('common.edit')" (click)="openEditDialog(row)"></button>
                <button pButton type="button" severity="secondary" size="small" [label]="t('dynamic.storefrontCatalog.manageOfferings')" (click)="openOfferingsDialog(row)"></button>
                <button pButton type="button" severity="danger" size="small" [label]="t('common.delete')" (click)="confirmDelete(row)"></button>
              </td>
            </tr>
          </ng-template>
        </p-table>
      }
    </ain-page>

    <p-dialog
      [(visible)]="createDialogVisible"
      [header]="t('dynamic.storefrontCatalog.createTitle')"
      [modal]="true"
      [style]="{ width: '32rem' }">
      <div class="form-grid">
        <label>
          <span>{{ t('dynamic.storefrontCatalog.fields.publication') }}</span>
          <p-select
            [options]="publicationOptions()"
            optionLabel="label"
            optionValue="value"
            [(ngModel)]="createForm.publicationId"
            name="createPublicationId"
            [filter]="true"
            appendTo="body"
            [placeholder]="t('dynamic.storefrontCatalog.selectPublication')" />
        </label>
        <label>
          <span>{{ t('dynamic.storefrontCatalog.fields.category') }}</span>
          <p-select
            [options]="categoryOptions()"
            optionLabel="label"
            optionValue="value"
            [(ngModel)]="createForm.categoryKey"
            name="createCategoryKey"
            appendTo="body" />
        </label>
        <label>
          <span>{{ t('dynamic.storefrontCatalog.fields.displayTitle') }}</span>
          <input pInputText class="w-full" [(ngModel)]="createForm.displayTitle" name="createDisplayTitle" />
        </label>
        <label>
          <span>{{ t('dynamic.storefrontCatalog.fields.teaser') }}</span>
          <textarea pTextarea class="w-full" rows="3" [(ngModel)]="createForm.teaser" name="createTeaser"></textarea>
        </label>
        <label>
          <span>{{ t('dynamic.storefrontCatalog.fields.sortOrder') }}</span>
          <p-inputNumber [(ngModel)]="createForm.sortOrder" name="createSortOrder" [useGrouping]="false" />
        </label>
        <label class="checkbox-row">
          <p-checkbox [(ngModel)]="createForm.isVisible" name="createIsVisible" [binary]="true" />
          <span>{{ t('dynamic.storefrontCatalog.fields.visible') }}</span>
        </label>
      </div>
      <ng-template pTemplate="footer">
        <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="createDialogVisible = false"></button>
        <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="saveCreate()"></button>
      </ng-template>
    </p-dialog>

    <p-dialog
      [(visible)]="editDialogVisible"
      [header]="t('dynamic.storefrontCatalog.editTitle')"
      [modal]="true"
      [style]="{ width: '32rem' }">
      @if (editingItem(); as item) {
        <div class="form-grid">
          <p class="meta">{{ item.formName }} · /forms/{{ item.publicationSlug }}</p>
          <label>
            <span>{{ t('dynamic.storefrontCatalog.fields.category') }}</span>
            <p-select
              [options]="categoryOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="editForm.categoryKey"
              name="editCategoryKey"
              appendTo="body" />
          </label>
          <label>
            <span>{{ t('dynamic.storefrontCatalog.fields.displayTitle') }}</span>
            <input pInputText class="w-full" [(ngModel)]="editForm.displayTitle" name="editDisplayTitle" />
          </label>
          <label>
            <span>{{ t('dynamic.storefrontCatalog.fields.teaser') }}</span>
            <textarea pTextarea class="w-full" rows="3" [(ngModel)]="editForm.teaser" name="editTeaser"></textarea>
          </label>
          <label>
            <span>{{ t('dynamic.storefrontCatalog.fields.sortOrder') }}</span>
            <p-inputNumber [(ngModel)]="editForm.sortOrder" name="editSortOrder" [useGrouping]="false" />
          </label>
          <label class="checkbox-row">
            <p-checkbox [(ngModel)]="editForm.isVisible" name="editIsVisible" [binary]="true" />
            <span>{{ t('dynamic.storefrontCatalog.fields.visible') }}</span>
          </label>
        </div>
      }
      <ng-template pTemplate="footer">
        <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="editDialogVisible = false"></button>
        <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="saveEdit()"></button>
      </ng-template>
    </p-dialog>

    <p-dialog
      [(visible)]="offeringsDialogVisible"
      [header]="t('dynamic.storefrontCatalog.offeringsTitle')"
      [modal]="true"
      [style]="{ width: '36rem' }">
      @if (editingItem(); as item) {
        <p class="meta">{{ item.displayTitle }}</p>
        <label>
          <span>{{ t('dynamic.storefrontCatalog.fields.offerings') }}</span>
          <p-multiSelect
            [options]="offeringOptions()"
            optionLabel="label"
            optionValue="value"
            [(ngModel)]="selectedOfferingIds"
            name="selectedOfferingIds"
            [filter]="true"
            appendTo="body"
            [placeholder]="t('dynamic.storefrontCatalog.selectOfferings')"
            class="w-full" />
        </label>
      }
      <ng-template pTemplate="footer">
        <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="offeringsDialogVisible = false"></button>
        <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="saveOfferings()"></button>
      </ng-template>
    </p-dialog>

    <p-confirmDialog />
  `,
  styles: [`
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 16px; }
    .actions { display: flex; gap: 6px; flex-wrap: wrap; }
    .form-grid { display: grid; gap: 12px; }
    .form-grid label { display: grid; gap: 6px; }
    .checkbox-row { display: flex; align-items: center; gap: 8px; }
    .w-full { width: 100%; }
    .meta { margin: 0 0 8px; color: var(--text-color-secondary); font-size: 0.9rem; }
    .row-warning td { background: #fff7ed; }
    .aggregate-panel { border: 1px solid var(--ain-color-border, #e5e7eb); border-radius: 14px; padding: 16px; margin-bottom: 16px; background: var(--ain-color-surface); }
    .aggregate-panel__head h2 { margin: 0 0 4px; font-size: 1.05rem; }
    .aggregate-panel__head p { margin: 0 0 12px; color: var(--text-color-secondary); }
  `]
})
export class StorefrontFormCatalogComponent implements OnInit {
  private readonly api = inject(DynamicApiService);
  private readonly commercialApi = inject(CommercialApiService);
  private readonly catalog = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly messages = inject(MessageService);
  private readonly route = inject(ActivatedRoute);

  readonly items = signal<StorefrontFormCatalogItem[]>([]);
  readonly industrySolutions = signal<IndustrySolution[]>([]);
  readonly industrySolutionsLoading = signal(false);
  readonly industrySolutionsError = signal<string | null>(null);
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly editingItem = signal<StorefrontFormCatalogItem | null>(null);
  readonly categoryKeys = signal<string[]>([]);
  readonly publications = signal<DynamicFormPublication[]>([]);
  readonly offerings = signal<CatalogOffering[]>([]);

  createDialogVisible = false;
  editDialogVisible = false;
  offeringsDialogVisible = false;
  selectedOfferingIds: string[] = [];

  createForm: CreateStorefrontFormCatalogItemRequest = this.emptyCreateForm();
  editForm: UpdateStorefrontFormCatalogItemRequest = this.emptyEditForm();

  readonly categoryOptions = computed<SelectOption[]>(() =>
    this.categoryKeys().map(key => ({
      value: key,
      label: this.categoryLabel(key)
    }))
  );

  readonly publicationOptions = computed<SelectOption[]>(() =>
    this.publications()
      .filter(item => item.isPublished && item.visibility === 'Public')
      .map(item => ({
        value: item.id,
        label: `/forms/${item.slug}`
      }))
  );

  readonly offeringOptions = computed<SelectOption[]>(() =>
    this.offerings().map(item => ({
      value: item.id,
      label: `${item.code} - ${item.title}`
    }))
  );

  ngOnInit(): void {
    this.loadReferenceData();
    this.load();
    this.loadIndustrySolutions();
    const publicationId = this.route.snapshot.queryParamMap.get('publicationId');
    if (publicationId) {
      this.createForm.publicationId = publicationId;
      this.createDialogVisible = true;
    }
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  categoryLabel(categoryKey: string): string {
    const key = `dynamic.storefrontCatalog.categories.${categoryKey}`;
    const translated = this.localization.translate(key);
    return translated === key ? categoryKey : translated;
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.api.listStorefrontCatalogItems().pipe(
      finalize(() => this.loading.set(false))
    ).subscribe({
      next: rows => this.items.set(rows),
      error: () => this.error.set(this.t('common.loadFailed'))
    });
  }

  loadIndustrySolutions(): void {
    this.industrySolutionsLoading.set(true);
    this.industrySolutionsError.set(null);
    this.commercialApi.listIndustrySolutions().pipe(
      finalize(() => this.industrySolutionsLoading.set(false))
    ).subscribe({
      next: rows => this.industrySolutions.set(rows),
      error: () => this.industrySolutionsError.set(this.t('dynamic.storefrontCatalog.industrySolutions.errorLoad'))
    });
  }

  resolvedSourceLabel(row: IndustrySolution): string {
    if ((row.assessmentLinks?.length ?? 0) > 0) {
      return this.t('dynamic.storefrontCatalog.industrySolutions.source.explicit');
    }

    if (row.useOfferingAssessmentFallback) {
      return this.t('dynamic.storefrontCatalog.industrySolutions.source.offeringFallback');
    }

    return this.t('dynamic.storefrontCatalog.industrySolutions.source.none');
  }

  openCreateDialog(): void {
    this.createForm = this.emptyCreateForm();
    const publicationId = this.route.snapshot.queryParamMap.get('publicationId');
    if (publicationId) {
      this.createForm.publicationId = publicationId;
    }
    this.createDialogVisible = true;
  }

  openEditDialog(row: StorefrontFormCatalogItem): void {
    this.editingItem.set(row);
    this.editForm = {
      categoryKey: row.categoryKey,
      displayTitle: row.displayTitle,
      teaser: row.teaser,
      sortOrder: row.sortOrder,
      isVisible: row.isVisible
    };
    this.editDialogVisible = true;
  }

  openOfferingsDialog(row: StorefrontFormCatalogItem): void {
    this.editingItem.set(row);
    this.selectedOfferingIds = row.offerings.map(item => item.offeringId);
    this.offeringsDialogVisible = true;
  }

  saveCreate(): void {
    if (!this.createForm.publicationId || !this.createForm.displayTitle?.trim()) {
      this.messages.add({ severity: 'warn', summary: this.t('dynamic.storefrontCatalog.validationRequired') });
      return;
    }

    this.saving.set(true);
    this.api.createStorefrontCatalogItem({
      ...this.createForm,
      displayTitle: this.createForm.displayTitle.trim(),
      teaser: this.createForm.teaser?.trim() ?? ''
    }).pipe(finalize(() => this.saving.set(false))).subscribe({
      next: () => {
        this.createDialogVisible = false;
        this.load();
        this.messages.add({ severity: 'success', summary: this.t('common.saved') });
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('common.saveFailed') })
    });
  }

  saveEdit(): void {
    const item = this.editingItem();
    if (!item || !this.editForm.displayTitle?.trim()) {
      return;
    }

    this.saving.set(true);
    this.api.updateStorefrontCatalogItem(item.id, {
      ...this.editForm,
      displayTitle: this.editForm.displayTitle.trim(),
      teaser: this.editForm.teaser?.trim() ?? ''
    }).pipe(finalize(() => this.saving.set(false))).subscribe({
      next: () => {
        this.editDialogVisible = false;
        this.load();
        this.messages.add({ severity: 'success', summary: this.t('common.saved') });
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('common.saveFailed') })
    });
  }

  saveOfferings(): void {
    const item = this.editingItem();
    if (!item) {
      return;
    }

    const payload: UpsertStorefrontFormCatalogOfferingLinkRequest[] = this.selectedOfferingIds.map((offeringId, index) => ({
      offeringId,
      sortOrder: index,
      isVisible: true
    }));

    this.saving.set(true);
    this.api.replaceStorefrontCatalogOfferings(item.id, payload).pipe(
      finalize(() => this.saving.set(false))
    ).subscribe({
      next: () => {
        this.offeringsDialogVisible = false;
        this.load();
        this.messages.add({ severity: 'success', summary: this.t('common.saved') });
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('common.saveFailed') })
    });
  }

  confirmDelete(row: StorefrontFormCatalogItem): void {
    this.confirmation.confirm({
      header: this.t('dynamic.storefrontCatalog.deleteConfirmTitle'),
      message: this.t('dynamic.storefrontCatalog.deleteConfirmMessage'),
      accept: () => {
        this.api.deleteStorefrontCatalogItem(row.id).subscribe({
          next: () => {
            this.load();
            this.messages.add({ severity: 'success', summary: this.t('dynamic.storefrontCatalog.deleted') });
          },
          error: () => this.messages.add({ severity: 'error', summary: this.t('common.saveFailed') })
        });
      }
    });
  }

  private loadReferenceData(): void {
    this.api.listStorefrontCatalogCategories().subscribe({
      next: rows => this.categoryKeys.set(rows.map(item => item.key)),
      error: () => this.categoryKeys.set(['other'])
    });
    this.api.listPublications().subscribe({
      next: rows => this.publications.set(rows),
      error: () => this.publications.set([])
    });
    this.catalog.listOfferings().subscribe({
      next: rows => this.offerings.set(rows),
      error: () => this.offerings.set([])
    });
  }

  private emptyCreateForm(): CreateStorefrontFormCatalogItemRequest {
    return {
      publicationId: '',
      categoryKey: 'other',
      displayTitle: '',
      teaser: '',
      sortOrder: 0,
      isVisible: true
    };
  }

  private emptyEditForm(): UpdateStorefrontFormCatalogItemRequest {
    return {
      categoryKey: 'other',
      displayTitle: '',
      teaser: '',
      sortOrder: 0,
      isVisible: true
    };
  }
}
